import { useState, useEffect, useContext, useMemo, useCallback } from "react";
import Select from "react-select";

import axios_api from "../Api";
import { API_URL, AUTH_TOKEN } from '../Context';


export default function TasksLoad(){

  const api_url = useContext(API_URL);
  const token = useContext(AUTH_TOKEN);

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const [startDate, setStartDate] = useState(today.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(tomorrow.toISOString().split('T')[0]);

  const [forms, setForms] = useState([]);
  const [labs, setLabs] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedCycle, setSelectedCycle] = useState('');
  const [selectedForm, setSelectedForm] = useState('');
  const [selectedLab, setSelectedLab] = useState('');
  const [newLabNumber, setNewLabNumber] = useState('');
  const [creatingLab, setCreatingLab] = useState(false);
  const [labCreateError, setLabCreateError] = useState(false);

  const [formError, setFormError] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);


  const loadLabs = useCallback(() => {
    return axios_api.get(api_url + '/users/', {
      headers: {"Authorization": token},
    })
    .then(
      (result) => { setLabs(result.data); return result.data; },
      (error) => { console.log(error); setLabs([]); return []; }
    );
  }, [api_url, token]);

  useEffect(() => {
    axios_api.get(api_url + '/forms/', {
      headers: {"Authorization": token},
    })
    .then(
      (result) => { setForms(result.data); },
      (error) => { console.log(error); }
    );

    loadLabs();
  }, [api_url, token, loadLabs]);


  const years = useMemo(() => {
    const unique = Array.from(new Set(forms.map((f) => f.year).filter(Boolean)));
    unique.sort((a, b) => b - a);
    return unique;
  }, [forms]);

  const cycles = useMemo(() => {
    const relevant = selectedYear
      ? forms.filter((f) => String(f.year) === String(selectedYear))
      : forms;
    const unique = Array.from(new Set(relevant.map((f) => f.cycle).filter((c) => c != null)));
    unique.sort((a, b) => a - b);
    return unique;
  }, [forms, selectedYear]);

  const formOptions = useMemo(() => {
    return forms
      .filter((f) => {
        if (selectedYear && String(f.year) !== String(selectedYear)) return false;
        if (selectedCycle && String(f.cycle) !== String(selectedCycle)) return false;
        return true;
      })
      .map((f) => {
        const catalogLabel = f.catalog_entry ? `[${f.catalog_entry}]` : '';
        return {
          value: String(f.id),
          label: `${f.form_code} - ${catalogLabel} - ${f.name}`,
          form_code: f.form_code || '',
          catalog_entry: f.catalog_entry || '',
        };
      });
  }, [forms, selectedYear, selectedCycle]);

  const selectedFormOption = useMemo(
    () => formOptions.find((option) => option.value === String(selectedForm)) || null,
    [formOptions, selectedForm],
  );

  const labOptions = useMemo(() => {
    return labs.map((lab) => ({
      value: lab.username,
      label: lab.username,
    }));
  }, [labs]);

  const selectedLabOption = useMemo(
    () => labOptions.find((option) => option.value === selectedLab) || null,
    [labOptions, selectedLab],
  );


  const handleChangeStartDate = (event) => {
    setStartDate(event.target.value);
    setSuccessMessage(false);
  };

  const handleChangeEndDate = (event) => {
    setEndDate(event.target.value);
    setSuccessMessage(false);
  };

  const handleChangeYear = (event) => {
    const yearValue = event.target.value;
    setSelectedYear(yearValue);
    setSelectedCycle('');
    setSelectedForm('');
    setSuccessMessage(false);
  };

  const handleChangeCycle = (event) => {
    setSelectedCycle(event.target.value);
    setSelectedForm('');
    setSuccessMessage(false);
  };

  const handleChangeForm = (option) => {
    setSelectedForm(option ? option.value : '');
    setSuccessMessage(false);
  };

  const handleChangeLab = (option) => {
    setSelectedLab(option ? option.value : '');
    setLabCreateError(false);
    setSuccessMessage(false);
  };

  const handleChangeNewLabNumber = (event) => {
    setNewLabNumber(event.target.value);
    setLabCreateError(false);
  };

  const handleCreateLab = (event) => {
    event.preventDefault();
    const labNumber = newLabNumber.trim();
    if (!labNumber) {
      setLabCreateError('Укажите номер лаборатории');
      return;
    }

    const labNumberInt = Number(labNumber);
    if (Number.isInteger(labNumberInt) && labNumberInt >= 80000 && labNumberInt <= 89999) {
      setLabCreateError('Диапазон номеров 80000–89999 зарезервирован для экспертов');
      return;
    }

    setCreatingLab(true);
    setLabCreateError(false);

    axios_api.post(api_url + '/manage_lab/', { lab: labNumber }, {
      headers: {
        "Authorization": token,
        "Content-Type": "application/json",
      },
    })
    .then(
      (result) => {
        const createdUsername = result.data.username;
        setCreatingLab(false);
        setNewLabNumber('');
        setLabCreateError(false);
        loadLabs().then(() => {
          setSelectedLab(createdUsername);
        });
      },
      (error) => {
        setCreatingLab(false);
        const message = error.response?.data || 'Не удалось создать лабораторию';
        setLabCreateError(typeof message === 'string' ? message : JSON.stringify(message));
        console.log(error);
      },
    );
  };

  const selectStyles = {
    container: (base) => ({ ...base, width: '100%' }),
    control: (base) => ({
      ...base,
      minHeight: '38px',
      borderColor: '#ced4da',
      boxShadow: 'none',
      '&:hover': { borderColor: '#86b7fe' },
    }),
  };

  const filterFormOption = (option, inputValue) => {
    const search = inputValue.trim().toLowerCase();
    if (!search) return true;
    const formCode = option.data.form_code.toLowerCase();
    const catalogEntry = option.data.catalog_entry.toLowerCase();
    const label = option.label.toLowerCase();
    return formCode.includes(search) || catalogEntry.includes(search) || label.includes(search);
  };

  const filterLabOption = (option, inputValue) => {
    const search = inputValue.trim().toLowerCase();
    if (!search) return true;
    return option.label.toLowerCase().includes(search);
  };


  const handleSubmit = (event) => {
    event.preventDefault(); // Предотвращаем перезагрузку страницы

    let params = {start_date: startDate, end_date: endDate};

    if (selectedForm) {
      params.form = selectedForm;
    }
    if (selectedLab) {
      params.lab = selectedLab;
    }

    axios_api.post(api_url + `/sync_tasks/`, params, {
      headers: {
        "Authorization": token, 
        "Content-Type": "application/json",
      }
    })
    .then(
      (result) => {
        console.log("Task loading procedure started");
        setFormError(false)
        setSuccessMessage(true);
      },
      (error) => {
        setFormError(error.response.data)
        setSuccessMessage(false);
        console.log(error);
      }
    );
  }


  return (
    <>
      <div className="row">
        <h3 className="m-3">Загрузить задания из 1С</h3>
      </div>
      
      {successMessage && 
        <div class="alert alert-success m-3 py-2" role="alert" style={{width: '500px'}}>
          <div className="text-success">
            <p>Задача на синхронизацию заданий запущена</p> 
            Результат отобразится в административной панели
          </div>
        </div>
      }
      <div className='row'>
        <div className="col-8 ms-3 lk-form">
          <form onSubmit={handleSubmit}>

            <div className="input-group my-3 d-flex gap-3">
              <label className="mt-2">
                <p>дата начала</p>
                <input
                  type="date"
                  value={startDate}
                  onChange={handleChangeStartDate}
                />
                <span className="ms-2">00:00</span>
              </label>

              <label className="ms-3 mt-2">
                <p>дата окончания</p>
                <input
                  type="date"
                  value={endDate}
                  onChange={handleChangeEndDate}
                />
                <span className="ms-2">00:00</span>
              </label>
            </div>

            <div className="input-group mt-5 mb-3 my-3 d-flex gap-3 ">
              <label className="mt-3">
                <p>год</p>
                <select className="lk-select form-select" value={selectedYear} onChange={handleChangeYear}>
                  <option value="">- все годы -</option>
                  {years.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </label>

              <label className="ms-2 mt-3">
                <p>цикл</p>
                <select
                  className="lk-select form-select"
                  value={selectedCycle}
                  onChange={handleChangeCycle}
                  disabled={!selectedYear}
                >
                  <option value="">- все циклы -</option>
                  {cycles.map((cycle) => (
                    <option key={cycle} value={cycle}>цикл {cycle}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="input-group mt-0 my-3">
              <label className="mt-3 w-100">
                <p>форма</p>
                <Select
                  className="lk-react-select"
                  classNamePrefix="lk-react-select"
                  options={formOptions}
                  value={selectedFormOption}
                  onChange={handleChangeForm}
                  filterOption={filterFormOption}
                  isClearable
                  isSearchable
                  placeholder="- все формы -"
                  noOptionsMessage={() => "Формы не найдены"}
                  styles={selectStyles}
                />
              </label>
            </div>

            <div className="input-group mt-5 my-3 d-flex align-items-end">
              <label className="mt-3" style={{ maxWidth: '300px', width: '100%' }}>
                <p>лаборатория</p>
                <Select
                  className="lk-react-select"
                  classNamePrefix="lk-react-select"
                  options={labOptions}
                  value={selectedLabOption}
                  onChange={handleChangeLab}
                  filterOption={filterLabOption}
                  isClearable
                  isSearchable
                  placeholder="- все лаборатории -"
                  noOptionsMessage={() => "Лаборатории не найдены"}
                  styles={{
                    ...selectStyles,
                    container: (base) => ({ ...base, width: '100%', maxWidth: '300px' }),
                  }}
                />
              </label>
              <label className="ms-3 mt-3">
                <p>добавить лабораторию</p>
                <input
                  type="text"
                  className="form-control"
                  style={{ maxWidth: '200px' }}
                  value={newLabNumber}
                  onChange={handleChangeNewLabNumber}
                  placeholder="код участника"
                  maxLength={5}
                  disabled={creatingLab}
                />
              </label>
              <button
                type="button"
                className="btn lk-button mb-0"
                onClick={handleCreateLab}
                disabled={creatingLab || newLabNumber.trim().length !== 5}
              >
                {creatingLab ? 'Проверка…' : 'Добавить'}
              </button>
            </div>

            {labCreateError &&
              <p className="text-danger">{labCreateError}</p>
            }

            {formError &&
              <p className="text-danger">{formError}</p>
            }
            
            {successMessage ?
              <button type="submit" className="btn lk-button my-3" disabled>Загрузить задания</button>
            :
              <button type="submit" className="btn lk-button my-3">Загрузить задания</button>
            }
          </form>
        </div>               
      </div> 
    </>
  );
}
